/**
 * Undo/redo history. One step = one apply() call. A step stores the changes that revert it
 * (`undo`) and the changes that perform it again (`redo`), both computed from the before/after
 * state of every touched object — so undo and redo are ordinary mutations (fresh HLC, synced).
 *
 * After an undo the engine computes a new step from what the undo ACTUALLY changed and stores its
 * inverse as the redo entry (and vice versa), so interleaved remote edits or dropped changes
 * never leave the stacks inconsistent.
 */
import type { AnnotationObject } from '../../../shared/annotations';
import type { Change } from '../contract';

export const UNDO_LIMIT = 200;

export interface Touched {
  id: string;
  /** State before the first change in this step (undefined = did not exist). Tombstones count as absent. */
  before: AnnotationObject | undefined;
  /** State after the last change in this step. */
  after: AnnotationObject | undefined;
}

export interface UndoStep {
  undo: Change[];
  redo: Change[];
}

const live = (o: AnnotationObject | undefined) => (o && o.deletedAt === null ? o : undefined);

function sameContent(a: AnnotationObject, b: AnnotationObject): boolean {
  return a.page === b.page && (a.data === b.data || JSON.stringify(a.data) === JSON.stringify(b.data));
}

/** The change that turns state `from` into state `to` (null when nothing differs). */
export function changeBetween(id: string, from: AnnotationObject | undefined, to: AnnotationObject | undefined): Change | null {
  const f = live(from);
  const t = live(to);
  if (!f && !t) return null;
  if (!t) return { type: 'delete', id };
  if (!f) return { type: 'add', obj: t };
  if (sameContent(f, t)) return null;
  return { type: 'update', id, page: t.page, data: t.data };
}

/** Builds an undo step from the touched objects of one apply() (null when nothing changed). */
export function stepFromTouched(touched: readonly Touched[]): UndoStep | null {
  const redo: Change[] = [];
  const undo: Change[] = [];
  for (const t of touched) {
    const r = changeBetween(t.id, t.before, t.after);
    if (!r) continue;
    redo.push(r);
    const u = changeBetween(t.id, t.after, t.before);
    if (u) undo.push(u);
  }
  if (!redo.length) return null;
  undo.reverse();
  return { undo, redo };
}

/** Swaps the direction of a step (what an undo did becomes the redo entry). */
export const invertStep = (s: UndoStep): UndoStep => ({ undo: s.redo, redo: s.undo });

const changeId = (c: Change) => (c.type === 'add' ? c.obj.id : c.id);

export class UndoHistory {
  private past: UndoStep[] = [];
  private future: UndoStep[] = [];

  constructor(private readonly limit = UNDO_LIMIT) {}

  get canUndo(): boolean {
    return this.past.length > 0;
  }
  get canRedo(): boolean {
    return this.future.length > 0;
  }
  get depth(): { undo: number; redo: number } {
    return { undo: this.past.length, redo: this.future.length };
  }

  /** A new user edit: push and forget the redo branch. */
  record(step: UndoStep): void {
    this.pushUndo(step);
    this.future = [];
  }

  /** Push without touching the redo stack (used after a redo). */
  pushUndo(step: UndoStep): void {
    this.past.push(step);
    if (this.past.length > this.limit) this.past.splice(0, this.past.length - this.limit);
  }

  pushRedo(step: UndoStep): void {
    this.future.push(step);
    if (this.future.length > this.limit) this.future.splice(0, this.future.length - this.limit);
  }

  popUndo(): UndoStep | undefined {
    return this.past.pop();
  }

  popRedo(): UndoStep | undefined {
    return this.future.pop();
  }

  /** Drops changes on the given object ids (e.g. a deleted layer); empty steps disappear. */
  prune(ids: ReadonlySet<string>): void {
    if (!ids.size) return;
    const fix = (steps: UndoStep[]) =>
      steps
        .map((s) => ({ undo: s.undo.filter((c) => !ids.has(changeId(c))), redo: s.redo.filter((c) => !ids.has(changeId(c))) }))
        .filter((s) => s.undo.length > 0 || s.redo.length > 0);
    this.past = fix(this.past);
    this.future = fix(this.future);
  }

  clear(): void {
    this.past = [];
    this.future = [];
  }
}
